import React, { useEffect, useState } from 'react';
import { CheckCircle2, RefreshCw, X } from 'lucide-react';
import { getDeviceInfo } from '../utils/deviceUtils';
import { getCurrentSession } from '../services/api/auth';
import { STALE_PERMISSIONS_EVENT, wasUpdatedBySw } from '../utils/staleAppGuard';

/**
 * Banner no intrusivo de "nueva versión disponible". Lo dispara index.tsx cuando
 * el Service Worker detecta código nuevo (deploy), vía el evento window
 * 'app-update-available'. Evita que el usuario tenga que recargar a mano para
 * tomar la última versión: aparece un aviso y recarga solo si el usuario lo toca.
 *
 * v6.11.37.00 — Reportado por QA: "estoy viendo desde la web y me dice que hay una
 * nueva versión de la app… no hay app, es web". El banner se monta en la raíz
 * (fuera del router), así que también aparecía para el visitante anónimo de la web
 * pública, que no instaló nada y no tiene por qué enterarse del service worker.
 * Ahora:
 *   - No se muestra a visitantes públicos sin sesión y sin PWA instalada. La
 *     versión nueva entra sola en la próxima navegación, sin pedirle nada.
 *   - Cuando sí se muestra, el copy dice "app" o "sitio" según el contexto real.
 *
 * v6.11.218 — dos avisos más (ver utils/staleAppGuard.ts):
 *   - "stale": un pedido a la base volvió rechazado porque ya no se muestran el
 *     DNI/teléfono/nacimiento → "Esta pantalla necesita recargarse" + botón
 *     "Recargar", a TODOS (también al visitante público: sin recargar, la
 *     pantalla queda vacía).
 *   - "updated": el service worker recargó sola una ventana con una versión
 *     vieja → "Actualizamos la app…" unos segundos, para que la recarga no
 *     parezca un error.
 */
type BannerKind = 'update' | 'stale' | 'updated';

/** Cuánto queda a la vista el aviso de "ya actualizamos". */
const UPDATED_NOTICE_MS = 6000;

export const AppUpdateBanner: React.FC = () => {
  const [kind, setKind] = useState<BannerKind | null>(() => (wasUpdatedBySw() ? 'updated' : null));

  useEffect(() => {
    const onUpdate = () => setKind((prev) => (prev === 'stale' ? prev : 'update'));
    const onStale = () => setKind('stale');
    window.addEventListener('app-update-available', onUpdate);
    window.addEventListener(STALE_PERMISSIONS_EVENT, onStale);
    return () => {
      window.removeEventListener('app-update-available', onUpdate);
      window.removeEventListener(STALE_PERMISSIONS_EVENT, onStale);
    };
  }, []);

  useEffect(() => {
    if (kind !== 'updated') return;
    const t = setTimeout(() => setKind((prev) => (prev === 'updated' ? null : prev)), UPDATED_NOTICE_MS);
    return () => clearTimeout(t);
  }, [kind]);

  if (!kind) return null;

  if (kind === 'stale') {
    return (
      <div className="fixed inset-x-0 bottom-24 md:bottom-6 z-[60] flex justify-center px-4 animate-slide-up pointer-events-none">
        <div role="alert" className="flex items-center gap-3 bg-slate-900 text-white rounded-2xl shadow-[var(--shadow-float)] px-4 py-3 max-w-sm w-full pointer-events-auto border border-white/10">
          <RefreshCw size={18} className="text-[var(--color-secondary)] shrink-0" />
          <p className="text-sm font-medium flex-1 leading-tight">
            Esta pantalla necesita recargarse para mostrar los datos.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[var(--color-secondary)] text-[var(--color-primary)] font-bold text-xs uppercase tracking-wide rounded-lg px-3 py-1.5 active:scale-95 transition-transform shrink-0"
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }

  if (kind === 'updated') {
    return (
      <div className="fixed inset-x-0 bottom-24 md:bottom-6 z-[60] flex justify-center px-4 animate-slide-up pointer-events-none">
        <div role="status" className="flex items-center gap-3 bg-slate-900 text-white rounded-2xl shadow-[var(--shadow-float)] px-4 py-3 max-w-sm w-full pointer-events-auto border border-white/10">
          <CheckCircle2 size={18} className="text-[var(--color-secondary)] shrink-0" />
          <p className="text-sm font-medium flex-1 leading-tight">
            Actualizamos la app a la última versión.
          </p>
          <button
            onClick={() => setKind(null)}
            aria-label="Cerrar aviso"
            className="text-white/50 hover:text-white shrink-0 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Corre instalada como PWA → es literalmente "la app".
  const isStandalone = getDeviceInfo().isStandalone;
  // Con sesión de participante es un usuario de la app aunque esté en el navegador:
  // le sirve saber que hay versión nueva antes de cargar un resultado.
  const hasSession = !!getCurrentSession()?.profile;

  // Visitante público: no le mostramos nada.
  if (!isStandalone && !hasSession) return null;

  const target = isStandalone ? 'la app' : 'el sitio';

  return (
    <div className="fixed inset-x-0 bottom-24 md:bottom-6 z-[60] flex justify-center px-4 animate-slide-up pointer-events-none">
      <div className="flex items-center gap-3 bg-slate-900 text-white rounded-2xl shadow-[var(--shadow-float)] px-4 py-3 max-w-sm w-full pointer-events-auto border border-white/10">
        <RefreshCw size={18} className="text-[var(--color-secondary)] shrink-0" />
        <p className="text-sm font-medium flex-1 leading-tight">
          Hay una nueva versión de {target}.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-[var(--color-secondary)] text-[var(--color-primary)] font-bold text-xs uppercase tracking-wide rounded-lg px-3 py-1.5 active:scale-95 transition-transform shrink-0"
        >
          Actualizar
        </button>
        <button
          onClick={() => setKind(null)}
          aria-label="Cerrar aviso"
          className="text-white/50 hover:text-white shrink-0 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
